import PropTypes from 'prop-types';
import { ProgressBar } from 'react-bootstrap';
import React, { useMemo } from 'react';

import { formatMonthYear } from '@/shared/utils/dateHelpers';
import './TemporalRangeBar.css';

const REFERENCE_START_YEAR = 2000;

/**
 * Extracts the fractional year from a date string (e.g., "2013-06-15" -> ~2013.45).
 * Returns null if the input is invalid.
 */
function toFractionalYear(dateString) {
    if (!dateString) return null;
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return null;
    const year = date.getFullYear();
    const startOfYear = new Date(year, 0, 1);
    const endOfYear = new Date(year + 1, 0, 1);
    const fraction =
        (date.getTime() - startOfYear.getTime()) /
        (endOfYear.getTime() - startOfYear.getTime());
    return year + fraction;
}

/**
 * TemporalRangeBar visualizes a temporal date range as a horizontal bar/timeline.
 *
 * The bar shows the data coverage period as a filled portion relative to a
 * reference range (2000 to the current year). If newestDate is null, the range
 * is treated as ongoing and extends to the present.
 */
export default function TemporalRangeBar({
    earliestDate = null,
    newestDate = null,
}) {
    const currentYear = new Date().getFullYear();
    const referenceEndYear = currentYear + 1;

    const computed = useMemo(() => {
        const startFrac = toFractionalYear(earliestDate);
        const endFrac = toFractionalYear(newestDate);
        const formattedStart = formatMonthYear(earliestDate);
        const formattedEnd = formatMonthYear(newestDate);

        // If both dates are null or invalid, don't render
        if (!startFrac && !endFrac) return null;

        const totalSpan = referenceEndYear - REFERENCE_START_YEAR;
        const isOngoing = startFrac && !endFrac;

        // Determine the effective start and end for bar positioning
        const effectiveStart = startFrac || REFERENCE_START_YEAR;
        const effectiveEnd = endFrac || referenceEndYear;

        // Calculate percentages within the reference range, clamped to [0, 100]
        const offsetPercent = Math.max(
            0,
            Math.min(
                100,
                ((effectiveStart - REFERENCE_START_YEAR) / totalSpan) * 100
            )
        );
        const rangePercent = Math.max(
            0,
            Math.min(
                100 - offsetPercent,
                ((effectiveEnd - effectiveStart) / totalSpan) * 100
            )
        );

        // Build the label text
        let label;
        if (formattedStart && formattedEnd) {
            label = `${formattedStart} — ${formattedEnd}`;
        } else if (formattedStart && isOngoing) {
            label = `${formattedStart} — Present`;
        } else if (formattedEnd) {
            label = formattedEnd;
        } else {
            label = formattedStart;
        }

        return {
            offsetPercent,
            rangePercent,
            label,
            isOngoing,
        };
    }, [earliestDate, newestDate, referenceEndYear]);

    if (!computed) return null;

    const { offsetPercent, rangePercent, label, isOngoing } = computed;
    const barVariant = isOngoing ? 'info' : 'primary';

    return (
        <div
            className="temporal-range-bar"
            role="figure"
            aria-label={`Temporal coverage: ${label}`}
        >
            <ProgressBar className="mb-1" aria-hidden="true">
                {/* Invisible spacer bar to create left offset */}
                <ProgressBar
                    now={offsetPercent}
                    key="spacer"
                    className="progress-bar-spacer"
                    aria-hidden="true"
                />
                {/* Visible bar representing the data coverage range */}
                <ProgressBar
                    variant={barVariant}
                    now={rangePercent}
                    key="range"
                    striped={isOngoing}
                    animated={isOngoing}
                    aria-hidden="true"
                />
            </ProgressBar>
            <div className="d-flex justify-content-between align-items-center">
                <small className="text-muted">{label}</small>
                {isOngoing && (
                    <span className="badge bg-info-subtle text-info-emphasis rounded-pill">
                        Ongoing
                    </span>
                )}
            </div>
        </div>
    );
}

TemporalRangeBar.propTypes = {
    /** The earliest date of the temporal range (ISO date string or null) */
    earliestDate: PropTypes.string,
    /** The newest date of the temporal range (ISO date string or null) */
    newestDate: PropTypes.string,
};
